import React from "react";
import { useHistory } from "react-router";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { Dialog } from "@syos-music/common";
import { TextField, SelectField } from "@syos-music/form";

import { Button, Grid, MenuItem } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import IntlUrlField from "components/form/IntlUrlField";

const MUTATION = gql`
  mutation (
    $type: String!
    $url: IntlStringInput!
    $section: ID
    $artistName: String
  ) {
    createArticle: CreateArticle(
      type: $type
      url: $url
      section: $section
      artistName: $artistName
    ) {
      article {
        id
      }
    }
  }
`;

const QUERY = gql`
  query {
    articleSections {
      edges {
        cursor
        node {
          id
          name
          label {
            fr
          }
        }
      }
    }
  }
`;

export default function CreateArticle({ type, children }) {
  const history = useHistory();

  const [isOpen, setIsOpen] = React.useState(false);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const [createArticle] = useMutation(MUTATION);

  const { data } = useQuery(QUERY, { skip: !open });

  const getArticleSection = (articleSectionId) =>
    data?.articleSections.edges.find(({ node }) => node.id === articleSectionId)
      ?.node;

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog open={isOpen} onClose={close}>
        <Form
          onSubmit={(values) =>
            createArticle({
              variables: values,
            })
              .then(({ data }) => {
                close();
                history.push(`/articles/${data.createArticle.article.id}`);
              })
              .catch(() => true)
          }
          initialValues={{
            type,
            url: { fr: "", en: "" },
          }}
          keepDirtyOnReinitialize
        >
          {({ handleSubmit, submitting, pristine, form, values }) => {
            let pathnamePrefix = "";
            switch (type) {
              case "artist":
                pathnamePrefix = "/artists/";
                break;
              case "job":
                pathnamePrefix = "/jobs/";
                break;

              case "blogPost": {
                const articleSection = getArticleSection(values.section);
                const sectionName = articleSection?.name ?? "_NO_BLOG_SECTION_";
                pathnamePrefix = `/blog/${sectionName}/`;
                break;
              }

              case "page":
              default:
                break;
            }

            return (
              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Dialog.Title color="primary">Créer un contenu</Dialog.Title>

                <Dialog.Content>
                  <Grid container>
                    {type === "blogPost" && (
                      <Grid item xs={12}>
                        <SelectField name="section" label="Rubrique" required>
                          {data?.articleSections.edges.map(
                            ({ cursor, node }) => (
                              <MenuItem key={cursor} value={node.id}>
                                {node.label.fr}
                              </MenuItem>
                            )
                          )}
                        </SelectField>

                        <OnChange name="section">
                          {(sectionId) => {
                            const newSection = getArticleSection(sectionId);

                            const re = new RegExp(`/blog/.*?/`);
                            const prefix = `/blog/${newSection.name}/`;

                            form.change("url", {
                              fr: values.url?.fr?.replace(re, prefix),
                              en: values.url?.en?.replace(re, prefix),
                            });
                          }}
                        </OnChange>
                      </Grid>
                    )}

                    {type === "artist" && (
                      <Grid item xs={12}>
                        <TextField
                          name="artistName"
                          label="Nom de l'artiste"
                          required
                        />
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <IntlUrlField
                        name="url"
                        pathnamePrefix={pathnamePrefix}
                        required
                        label="URL"
                      />
                    </Grid>
                  </Grid>
                </Dialog.Content>

                <Dialog.Actions>
                  <Button variant="text" onClick={close}>
                    Annuler
                  </Button>

                  <LoadingButton
                    type="submit"
                    loading={submitting}
                    disabled={pristine}
                  >
                    Créer
                  </LoadingButton>
                </Dialog.Actions>
              </form>
            );
          }}
        </Form>
      </Dialog>
    </>
  );
}
